import { isEmpty, isNil } from 'lodash';
import { OrganizationAppSubscription } from '~components/displays/BulkAction/BulkManageApps';
import { PlaybackSection_DisplayFragment } from '~components/displays/DisplayDetail/playback/__generated__/PlaybackSection.graphql';
import { AppSubscription } from '~components/displays/useManageAppSubscriptionsForm';
import { ConfigPower, DisplayPlatform, Power } from '~graphql/__generated__/types';
import {
  UseConfigContentSource_ConfigFragment,
  UseConfigContentSource_ContentSource_ConfigAppContentSource_Fragment,
  UseConfigContentSource_ContentSource_ConfigBookmarkContentSource_Fragment,
  UseConfigContentSource_ContentSource_ConfigInputContentSource_Fragment,
  UseConfigContentSource_ContentSource_ConfigPlaylistContentSource_Fragment,
  UseConfigContentSource_ContentSourceFragment,
} from './__generated__/useContentSource.graphql';
import { ConfigToggleOptionsConfig } from './ConfigurationDetail/ConfigDisplayControlsToggle';

export function mapStringToPowerEnum(value: string) {
  switch (value) {
    case 'ON':
      return ConfigPower.On;
    case 'STANDBY':
      return ConfigPower.Standby;
    default:
      throw new Error(`Power value not recognized: ${value}`);
  }
}

export function mapPowerEnumToLabel(power: Power): string {
  switch (power) {
    case Power.On:
      return 'On';
    case Power.Standby:
      return 'Standby';
  }
}

export function getPlatformLabel(platform: Pick<DisplayPlatform, 'name' | 'version'>) {
  return [platform.name, platform.version]
    .filter((value) => !isNil(value) && value !== 'undefined')
    .join(' - ');
}

export function formatDownloadProgress(progress: number) {
  return `${Math.round((progress + Number.EPSILON) * 100)}%`;
}

export function isBrowserSource(source: string | undefined) {
  return source?.toLowerCase() === 'browser';
}

export function isMediaPlayerSource(source: string | undefined) {
  return source?.toLowerCase() === 'media player';
}

export function isContentSourcesSupported(
  display: UseConfigContentSource_ConfigFragment | PlaybackSection_DisplayFragment,
): boolean {
  return !isEmpty(display.contentSource?.available);
}
export function isDefaultContentSourcesSupported(
  display: UseConfigContentSource_ConfigFragment | PlaybackSection_DisplayFragment,
): boolean {
  return !isEmpty(display.contentSource?.default);
}

export function isAppContentSource(
  source: UseConfigContentSource_ContentSourceFragment | undefined,
): source is UseConfigContentSource_ContentSource_ConfigAppContentSource_Fragment {
  return source?.__typename === 'ConfigAppContentSource';
}

export function isBookmarkContentSource(
  source: UseConfigContentSource_ContentSourceFragment | undefined,
): source is UseConfigContentSource_ContentSource_ConfigBookmarkContentSource_Fragment {
  return source?.__typename === 'ConfigBookmarkContentSource';
}

export function isInputContentSource(
  source: UseConfigContentSource_ContentSourceFragment | undefined,
): source is UseConfigContentSource_ContentSource_ConfigInputContentSource_Fragment {
  return source?.__typename === 'ConfigInputContentSource';
}

export function isPlaylistContentSource(
  source: UseConfigContentSource_ContentSourceFragment | undefined,
): source is UseConfigContentSource_ContentSource_ConfigPlaylistContentSource_Fragment {
  return source?.__typename === 'ConfigPlaylistContentSource';
}

export function contentSourceToStringValue(
  source: UseConfigContentSource_ContentSourceFragment,
): string {
  const value = `contentSource|${source.__typename}`;

  if (isAppContentSource(source)) {
    return [
      value,
      source.applicationId,
      ...(source.selectedActivity ? [source.selectedActivity] : []),
    ].join('|');
  }

  if (isBookmarkContentSource(source)) {
    return `${value}|${source.index}`;
  }

  if (isInputContentSource(source)) {
    return `${value}|${source.source}`;
  }

  if (isPlaylistContentSource(source)) {
    return `${value}|${source.playlistId}`;
  }

  throw new Error(`Unsupported input source: ${JSON.stringify(source, null, 2)}`);
}

export function toAvailableControlToggleOptions<T extends string>(
  defaultOptions: Array<ConfigToggleOptionsConfig<T>>,
  availableOptions: T[] = [],
) {
  if (availableOptions.length === 0) {
    return defaultOptions;
  }
  return defaultOptions.filter(({ value }) => availableOptions.includes(value));
}

export function getSubscriptionVersion(
  appSubscription: AppSubscription | OrganizationAppSubscription,
) {
  return 'appInstallation' in appSubscription &&
    (appSubscription.appInstallation?.__typename === 'AppInstallationInstalling' ||
      appSubscription.appInstallation?.__typename === 'AppInstallationInstalled' ||
      appSubscription.appInstallation?.__typename === 'AppInstallationUninstalling')
    ? appSubscription.appInstallation.versionName
    : null;
}
